"use client";

import type { ReactNode } from "react";
import { Lock } from "lucide-react";

/* ─────────────────────────────────────────────────────────────
 * Geräte-Rahmen für alle Mockups der Website (Landingpage und
 * Dienstleistungsseiten): Browser-Fenster und Smartphone.
 * ──────────────────────────────────────────────────────────── */

/** Browser-Fenster mit Ampel-Punkten und URL-Leiste. */
export function BrowserFrame({
  host,
  children,
  className = "",
  chromeStyle,
}: {
  host: string;
  children: ReactNode;
  className?: string;
  chromeStyle?: "light" | "dark";
}) {
  const dark = chromeStyle === "dark";
  return (
    <div
      className={`overflow-hidden rounded-xl border ${className}`}
      style={{
        borderColor: dark ? "rgba(255,255,255,0.08)" : "var(--border)",
        backgroundColor: dark ? "#101828" : "var(--card-bg)",
        boxShadow: "var(--card-shadow-lg)",
      }}
    >
      <div
        className="flex items-center gap-1.5 border-b px-3 py-2"
        style={{
          borderColor: dark ? "rgba(255,255,255,0.08)" : "var(--border)",
          backgroundColor: dark ? "#1D2939" : "var(--bg-secondary)",
        }}
      >
        <span className="h-2 w-2 rounded-full bg-[#FF5F57]" />
        <span className="h-2 w-2 rounded-full bg-[#FEBC2E]" />
        <span className="h-2 w-2 rounded-full bg-[#28C840]" />
        <div
          className="ml-2 flex flex-1 items-center gap-1.5 rounded-md px-2 py-0.5 text-[10px]"
          style={{
            backgroundColor: dark ? "#101828" : "var(--bg)",
            color: dark ? "#98A2B3" : "var(--text-tertiary)",
          }}
        >
          <Lock className="h-2.5 w-2.5" />
          <span className="truncate">{host}</span>
        </div>
      </div>
      {children}
    </div>
  );
}

/** Smartphone-Rahmen (für App- und Social-Ansichten). Position (relative/absolute) kommt vom Aufrufer. */
export function PhoneFrame({
  children,
  className = "",
  width = 150,
}: {
  children: ReactNode;
  className?: string;
  width?: number;
}) {
  return (
    <div
      className={`overflow-hidden rounded-[1.4rem] border-[5px] ${className}`}
      style={{
        width,
        aspectRatio: "9 / 19",
        borderColor: "#101828",
        backgroundColor: "var(--card-bg)",
        boxShadow: "0 18px 40px rgba(16,24,40,0.28)",
      }}
    >
      <div
        className="absolute left-1/2 top-1.5 z-10 h-1.5 w-8 -translate-x-1/2 rounded-full"
        style={{ backgroundColor: "#101828" }}
      />
      {children}
    </div>
  );
}
